import React, { useEffect, useMemo, useRef, useState } from "react";
import styles from "./formSelect.module.css";
import classNames from "classnames";

const FormSelect = ({
  label,

  name,

  value,

  onChange,

  onBlur,

  options = [],

  placeholder = "Select option",

  variant = "primary",

  size = "medium",

  width = "fullWidth",

  error,

  className,

  selectClassName,

  ...props
}) => {
  const containerRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const normalizedValue = value ?? "";
  const selectedOption = useMemo(
    () =>
      options.find((opt) => String(opt.value) === String(normalizedValue)) || null,
    [options, normalizedValue]
  );

  const filteredOptions = useMemo(() => {
    const query = String(searchTerm || "").toLowerCase().trim();
    if (!query) return options;
    return options.filter((opt) =>
      String(opt.label || "")
        .toLowerCase()
        .includes(query)
    );
  }, [options, searchTerm]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!containerRef.current?.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  const emitChange = (nextValue) => {
    if (onChange) {
      onChange({
        target: {
          name,
          value: nextValue,
        },
      });
    }
  };

  const handleSelect = (nextValue) => {
    emitChange(nextValue);
    setIsOpen(false);
    setSearchTerm("");
    if (onBlur) onBlur();
  };

  const handleInputFocus = () => {
    if (props.disabled) return;
    setSearchTerm("");
    setIsOpen(true);
  };

  const handleInputChange = (event) => {
    setSearchTerm(event.target.value);
    if (!isOpen) setIsOpen(true);
  };

  const displayValue = isOpen ? searchTerm : selectedOption?.label || "";

  return (
    <div
      ref={containerRef}
      className={classNames(styles.selectGroup, styles[width], className)}
    >
      {label && (
        <label htmlFor={name} className={styles.label}>
          {label}
        </label>
      )}

      <div className={styles.selectWrapper}>
        <input
          id={name}
          name={name}
          type="text"
          value={displayValue}
          onFocus={handleInputFocus}
          onBlur={() => {
            setTimeout(() => {
              setIsOpen(false);
              setSearchTerm("");
            }, 100);
            if (onBlur) onBlur();
          }}
          onChange={handleInputChange}
          placeholder={!selectedOption ? placeholder : ""}
          autoComplete="off"
          className={classNames(
            styles.selectInput,
            styles[variant],
            styles[size],
            selectClassName,
            {
              [styles.errorInput]: error,
            }
          )}
          {...props}
        />
        <button
          type="button"
          className={styles.toggleButton}
          onClick={() => {
            if (props.disabled) return;
            setIsOpen((prev) => {
              const next = !prev;
              if (next) setSearchTerm("");
              else setSearchTerm("");
              return next;
            });
          }}
          disabled={props.disabled}
          tabIndex={-1}
        >
          <i className={`bi ${isOpen ? "bi-chevron-up" : "bi-chevron-down"}`} />
        </button>
      </div>

      {isOpen && !props.disabled && (
        <div className={styles.optionsMenu}>
          <button
            type="button"
            className={classNames(styles.optionButton, styles.placeholderOption)}
            onMouseDown={(event) => {
              event.preventDefault();
              handleSelect("");
            }}
          >
            {placeholder}
          </button>
          {filteredOptions.length > 0 ? (
            filteredOptions.map((opt) => (
              <button
                type="button"
                key={opt.value}
                className={classNames(styles.optionButton, {
                  [styles.selectedOption]:
                    String(opt.value) === String(normalizedValue),
                })}
                onMouseDown={(event) => {
                  event.preventDefault();
                  handleSelect(opt.value);
                }}
              >
                {opt.label}
              </button>
            ))
          ) : (
            <div className={styles.emptyOption}>No results found</div>
          )}
        </div>
      )}

      <input
        type="hidden"
        name={name}
        value={normalizedValue}
        readOnly
      />

      {error && <div className={styles.errorText}>{error}</div>}
    </div>
  );
};

export default FormSelect;
